import layouts from "../data/layouts";
import { getConfig } from "./config";
import {
  initGam,
  determineBreakpoint,
  determineLayout,
  ajaxEventHandler,
} from "./functions";
import commentBannerInf from "../data/units/commentBannerInf";

export async function defineAdsController() {
  return new Promise<object>(async (resolve, reject) => {
    let googletag = initGam();
    let pageAds: adUnit[] = [];
    let pageLazyloadConfig: object = {};
    let gamSlots: object[] = [];
    let breakpoint = determineBreakpoint();
    let dynamicAds = [];
    const layout: string = determineLayout();
    const config = await getConfig();

    // Registers the event handler that listens to ajax events so we can refresh particular ads.
    ajaxEventHandler();

    // We define a custom event because the JS in the head needs to wait for DOMContentLoaded in order to access Drupal settings, but this will cause
    // the JS display calls to GPT in the body to execute first, out of order which errors. This ensures the order is correct - define then call.
    // Drupal settings is required to get info from PHP to JS such as user status and current template.
    const adsDefinedEvent = new CustomEvent("adsDefinedEvent", {
      detail: {},
      bubbles: true,
      cancelable: true,
      composed: false,
    });

    if (!layout || layout === "no layout") {
      return reject("No ads. No Layout for this page");
    }

    // Pull in the ads for this specific layout.
    pageAds = layouts[layout][breakpoint];

    // Create ads that are dynamic based on the number of placeholders generated and cannot be pre-defined.
    dynamicAds = createDynamicAds();

    pageAds = pageAds.concat(dynamicAds);

    // Set breakpoint specific lazy load configuration for GAM.
    pageLazyloadConfig = config.gam.lazyLoadSettings;

    // Create ad unit definitions for Amazon and Prebid.
    let apstagSlots: amazonUnit[] = generateAmazonUnits(pageAds, breakpoint);
    let prebidUnits: prebidUnit[] = generatePrebidUnits(pageAds);

    // Push the GAM slot definitions and settings to GAM queue. This will get called once GPT has loaded on the page.
    googletag.cmd.push(function () {
      let targeting = generateTargeting();

      if (targeting.length) {
        targeting.forEach(function (pair: any) {
          googletag.pubads().setTargeting(pair.key, pair.value);
        });
      }

      gamSlots = generateGamSlots(pageAds, breakpoint);
      googletag.pubads().enableLazyLoad(pageLazyloadConfig);

      googletag.pubads().addEventListener("slotRequested", function (event) {
        window.drupalSettings.ads.log.push(
          `${event.slot.getSlotElementId()} fetched`
        );
      });

      googletag
        .pubads()
        .addEventListener("slotResponseReceived", function (event) {
          window.drupalSettings.ads.log.push(
            `Ad response has been received for ${event.slot.getSlotElementId()}`
          );
        });

      googletag.pubads().addEventListener("slotOnload", function (event) {
        window.drupalSettings.ads.log.push(
          `${event.slot.getSlotElementId()} rendered`
        );
      });

      googletag.enableServices();
    });

    document.dispatchEvent(adsDefinedEvent);

    return resolve({
      amazonSlots: apstagSlots,
      prebidSlots: prebidUnits,
      googleSlots: gamSlots,
    });
  });
}

/**
 * Check for existence of dynamically generated DOM elements and create corresponding ad units.
 *  * @returns {Array} Ad units.
 */
function createDynamicAds() {
  const commentAdsToCreate = document.querySelectorAll(
    ".banner-placement-inf"
  ).length;
  let adUnits: object[] = [];

  // This lets us dynamically create the banner ads in the comment section
  // based on how many placeholders were created.
  if (commentAdsToCreate > 0) {
    for (let step = 1; step <= commentAdsToCreate; step++) {
      const placeholder = document.querySelector("#comment_banner_" + step);

      // Check if element exists - it may not due to house ad/resource.
      if (placeholder) {
        // We also need to dynamically create and inject the display() call to GPT.
        let script = document.createElement("script");
        script.innerHTML = `document.addEventListener("adsDefinedEvent", function() {
          window.googletag.cmd.push(function() { window.googletag.display("comment_banner_${step}"); });
        });`;
        placeholder.appendChild(script);
        let commentAd = new commentBannerInf(step);
        adUnits.push(commentAd);
      }
    }
  }

  return adUnits;
}

/**
 * Iterate through an array of units and generate Google Ad Manager slot definitions.
 * @param {Array} adUnits
 * @param breakpoint
 * @returns {Array} gamAdUnits
 */
function generateGamSlots(adUnits: adUnit[], breakpoint) {
  let gamAdUnits: object[] = [];

  adUnits.forEach(function (ad) {
    const mapping = generateSizeMappings(ad);

    if (ad.targeting) {
      const targetingValue = String(ad.targeting);

      gamAdUnits.push(
        window.googletag
          .defineSlot(ad.gamCode, ad.sizes[breakpoint], ad.element)
          .addService(window.googletag.pubads())
          .setTargeting("wso_position", targetingValue)
          .defineSizeMapping(mapping)
      );
    } else {
      gamAdUnits.push(
        window.googletag
          .defineSlot(ad.gamCode, ad.sizes[breakpoint], ad.element)
          .addService(window.googletag.pubads())
          .defineSizeMapping(mapping)
      );
    }
  });

  window.gamSlots = gamAdUnits;

  return gamAdUnits;
}

// Iterate through the ad units size mappings and build Google's size mapping.
function generateSizeMappings(ad: adUnit) {
  if (!ad.sizeMapping) {
    return;
  }

  const adUnitSizeMappings = ad.sizeMapping;
  const sizeMapping = window.googletag.sizeMapping();

  for (let i = 0; i < adUnitSizeMappings.length; i++) {
    const { viewPortSize, sizes } = adUnitSizeMappings[i];
    sizeMapping.addSize(viewPortSize, sizes);
  }

  return sizeMapping.build();
}

/**
 * Set page level targeting for the ad server.
 *  * @returns {Array} targetingParameters key-value pairs to be set.
 */
function generateTargeting() {
  let targetingParameters: object[] = [];
  let keyValue: object = {};

  if (window.drupalSettings) {
    let user = window.drupalSettings.ads.userLoggedIn.toString();

    if (user) {
      keyValue = {
        key: "wso-user-status",
        value: user,
      };

      targetingParameters.push(keyValue);
    }
  }

  keyValue = {
    key: "wso-forum-taxonomy",
    value: findForumTax(window.location.pathname),
  };

  targetingParameters.push(keyValue);

  return targetingParameters;
}

/**
 * Iterate through an array of units and generate Prebid ad unit definitions.
 * @param {Array} adUnits
 * @returns {Array} prebidUnits. 4 properties of a Prebid object - code, mediaTypes, labels, and bids.
 */
function generatePrebidUnits(adUnits: adUnit[]) {
  let prebidUnits: prebidUnit[] = [];

  adUnits.forEach(function (ad) {
    if (ad.vendors.prebid.enabled) {
      let prebidSchema: prebidUnit = {
        code: ad.gamCode,
        mediaTypes: ad.vendors.prebid.mediaTypes,
        bids: ad.vendors.prebid.bids,
      };

      prebidUnits.push(prebidSchema);
    }
  });

  return prebidUnits;
}

/**
 * Iterate through an array of units and generate Amazon ad unit definitions.
 * @param {Array} adUnits
 * @param breakpoint
 * @returns {Array} amazonUnits. 3 properties of an Amazon object - slotId (the element ID), slotName (the GAM code), and sizes (array of sizes).
 */
function generateAmazonUnits(adUnits: adUnit[], breakpoint) {
  let amazonUnits: amazonUnit[] = [];

  adUnits.forEach(function (ad) {
    if (ad.vendors.amazon) {
      let amazonSchema: amazonUnit = {
        slotID: ad.element,
        slotName: ad.gamCode,
        sizes: ad.sizes[breakpoint] as number[][],
      };

      amazonUnits.push(amazonSchema);
    }
  });

  return amazonUnits;
}

function findForumTax(path) {
  const paths = path.split("/");

  if (paths.indexOf("forum") === 1) {
    return paths[2];
  } else if (paths.indexOf("72-hours") === 3) {
    return "top-last-72";
  } else if (paths.indexOf("top") === 2) {
    return "top-all-time";
  } else if (paths.indexOf("tracker") === 1) {
    return "recently-active";
  } else {
    return "n/a";
  }
}
